/**
 * UranIA PA — SPID smart-button (US-010).
 *
 * Rende il bottone "Entra con SPID" Bootstrap Italia compliant:
 *   - <button> (semantic HTML, role implicito = button) con `aria-haspopup`,
 *     `aria-expanded`, `aria-controls` per il dropdown dei 9 IdP (AC#2).
 *   - Dropdown è una `<ul role="menu">` con 9 `<li role="none">` che
 *     contengono `<button role="menuitem">` cliccabili — pattern AgID Linee
 *     Guida SPID v2.0 §6.2 (UI standard 9 IdP modal).
 *   - Tastiera: <Esc> chiude, <ArrowDown>/<ArrowUp> non aggiunti in MVP
 *     (axe-core 0 serious è coperto da role/aria; full keyboard nav su
 *     prossima iter UX — fuori scope US-010 AC#5).
 *
 * NB: l'SPID logo ufficiale AgID è proprietario e richiede licenza
 * (AgID Manuale operativo §A.5). In Fase 1 (staging) usiamo il glyph
 * "SPID" testuale + classe `urania-spid-button` per lo styling. La
 * sostituzione con l'SVG ufficiale è puntuale e gated su completion
 * dell'iter AgID (runbook docs/runbooks/spid-onboarding.md §8).
 */

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { JSX } from 'react';

import { SPID_IDPS, type SpidIdpOption } from '../../lib/auth/idp-options.ts';

export interface SpidButtonProps {
  /** Invocato quando l'utente seleziona un IdP dal dropdown. */
  readonly onSelectIdp: (option: SpidIdpOption) => void;
  /** Disabilita interazioni (es. flusso OIDC in corso). */
  readonly disabled?: boolean;
}

export function SpidButton({ onSelectIdp, disabled = false }: SpidButtonProps): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const buttonId = useId();
  const menuId = `${buttonId}-menu`;

  const toggle = useCallback(() => {
    if (disabled) {
      return;
    }
    setOpen((prev) => !prev);
  }, [disabled]);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  // Chiude il dropdown al click fuori dal container.
  useEffect(() => {
    if (!open) {
      return;
    }
    function onDocumentClick(event: MouseEvent): void {
      const target = event.target;
      if (
        target instanceof Node &&
        containerRef.current &&
        !containerRef.current.contains(target)
      ) {
        setOpen(false);
      }
    }
    function onKeydown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocumentClick);
    document.addEventListener('keydown', onKeydown);
    return (): void => {
      document.removeEventListener('mousedown', onDocumentClick);
      document.removeEventListener('keydown', onKeydown);
    };
  }, [open]);

  const handleSelect = useCallback(
    (option: SpidIdpOption): void => {
      setOpen(false);
      onSelectIdp(option);
    },
    [onSelectIdp],
  );

  return (
    <div className="urania-spid-button-group" ref={containerRef}>
      <button
        type="button"
        id={buttonId}
        className="btn btn-primary urania-spid-button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        data-testid="spid-button"
        disabled={disabled}
        onClick={toggle}
      >
        <span className="urania-spid-button__brand" aria-hidden="true">
          SPID
        </span>
        <span className="urania-spid-button__label">{t('auth.login_with_spid')}</span>
        <span className="urania-spid-button__caret" aria-hidden="true">
          {open ? '▴' : '▾'}
        </span>
      </button>
      <ul
        id={menuId}
        role="menu"
        aria-labelledby={buttonId}
        aria-label={t('login.spid_select_idp_aria')}
        className="urania-spid-idp-menu"
        data-testid="spid-idp-menu"
        hidden={!open}
      >
        <li role="none" className="urania-spid-idp-menu__header">
          <span className="urania-spid-idp-menu__header-text">{t('login.spid_select_idp')}</span>
        </li>
        {SPID_IDPS.map((idp) => (
          <li role="none" key={idp.id}>
            <button
              type="button"
              role="menuitem"
              className="urania-spid-idp-menu__item"
              data-testid={`spid-idp-${idp.id}`}
              onClick={(): void => {
                handleSelect(idp);
              }}
            >
              {idp.label}
            </button>
          </li>
        ))}
        <li role="none" className="urania-spid-idp-menu__footer">
          <button
            type="button"
            className="urania-spid-idp-menu__close"
            data-testid="spid-close"
            onClick={close}
          >
            {t('login.spid_close_menu')}
          </button>
        </li>
      </ul>
    </div>
  );
}
